import { useState, useEffect, useCallback } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL || '';
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY || '';

const supabase = createClient(supabaseUrl, supabaseAnonKey);

/**
 * ファンプロフィールの型定義
 */
export interface UsersProfile {
  id: string;
  display_name?: string;
  preferred_call_name?: string;
  birthday?: string;
  created_at: string;
}

/**
 * Supabaseからファンプロフィールを管理するカスタムフック
 */
export function useSupabaseProfile(userId: string) {
  const [profile, setProfile] = useState<UsersProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * プロフィールを取得
   */
  const fetchProfile = useCallback(async () => {
    if (!userId || userId.trim() === '') {
      setProfile(null);
      return null;
    }
    try {
      const { data, error: fetchError } = await supabase
        .from('users_profile')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (fetchError) throw fetchError;
      setProfile(data);
      return data;
    } catch (err) {
      console.error('プロフィールの取得に失敗しました:', err);
      setError(err instanceof Error ? err.message : '不明なエラー');
      return null;
    }
  }, [userId]);

  /**
   * プロフィールを更新
   */
  const updateProfile = useCallback(async (
    updates: Partial<Omit<UsersProfile, 'id' | 'created_at'>>
  ): Promise<boolean> => {
    if (!userId || userId.trim() === '') return false;
    try {
      const { error: updateError } = await supabase
        .from('users_profile')
        .update(updates)
        .eq('id', userId);

      if (updateError) throw updateError;

      await fetchProfile();
      return true;
    } catch (err) {
      console.error('プロフィールの更新に失敗しました:', err);
      setError(err instanceof Error ? err.message : '不明なエラー');
      return false;
    }
  }, [userId, fetchProfile]);

  /**
   * プロフィールを作成
   */
  const createProfile = useCallback(async (
    data: Omit<UsersProfile, 'created_at'>
  ): Promise<boolean> => {
    if (!userId || userId.trim() === '') return false;
    try {
      const { error: insertError } = await supabase
        .from('users_profile')
        .insert(data);

      if (insertError) throw insertError;

      await fetchProfile();
      return true;
    } catch (err) {
      console.error('プロフィールの作成に失敗しました:', err);
      setError(err instanceof Error ? err.message : '不明なエラー');
      return false;
    }
  }, [fetchProfile]);

  /**
   * 初回読み込み
   */
  useEffect(() => {
    if (!userId || userId.trim() === '') {
      setIsLoading(false);
      return;
    }
    const loadData = async () => {
      setIsLoading(true);
      setError(null);
      await fetchProfile();
      setIsLoading(false);
    };

    loadData();
  }, [fetchProfile, userId]);

  return {
    profile,
    isLoading,
    error,
    fetchProfile,
    updateProfile,
    createProfile,
  };
}
